// src/components/Locations/LocationEventsTab.js
// Staff CRUD for activities, appointments, and events per location
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Chip, Tooltip, Alert, CircularProgress,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { residentEventsApi, personsApi } from '../../services/apiClient';
import { useThemeSettings } from '../../contexts/ThemeContext';

const EVENT_TYPES = [
  { value: 'activity', label: 'Activity', color: '#16A34A' },
  { value: 'appointment', label: 'Appointment', color: '#2563EB' },
  { value: 'reminder', label: 'Reminder', color: '#DC2626' },
  { value: 'birthday', label: 'Birthday', color: '#F59E0B' },
];

const INITIAL_FORM = {
  title: '',
  description: '',
  eventType: 'activity',
  startsAt: '',
  endsAt: '',
  allDay: false,
  locationName: '',
  maxParticipants: '',
  requiresSignup: false,
  color: '',
  personId: '',
};

const LocationEventsTab = ({ locationId }) => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [events, setEvents] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [editingEvent, setEditingEvent] = useState(null);

  // Signups dialog
  const [signupsDialogOpen, setSignupsDialogOpen] = useState(false);
  const [signupsEvent, setSignupsEvent] = useState(null);
  const [signups, setSignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);

  // Delete confirm
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  const fetchEvents = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await residentEventsApi.getAll({ locationId });
      setEvents(response.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  const fetchPersons = useCallback(async () => {
    if (!locationId) return;
    try {
      const response = await personsApi.getByLocation(locationId);
      setPersons(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to load persons:', err);
    }
  }, [locationId]);

  useEffect(() => {
    fetchEvents();
    fetchPersons();
  }, [fetchEvents, fetchPersons]);

  const handleOpenCreate = () => {
    setFormData(INITIAL_FORM);
    setEditingEvent(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleOpenEdit = (event) => {
    setEditingEvent(event);
    setDialogMode('edit');
    setFormData({
      title: event.title || '',
      description: event.description || '',
      eventType: event.eventType || 'activity',
      startsAt: event.startsAt ? event.startsAt.slice(0, 16) : '',
      endsAt: event.endsAt ? event.endsAt.slice(0, 16) : '',
      allDay: event.allDay || false,
      locationName: event.locationName || '',
      maxParticipants: event.maxParticipants || '',
      requiresSignup: event.requiresSignup || false,
      color: event.color || '',
      personId: event.personId || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.startsAt) {
      enqueueSnackbar('Title and start time are required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        locationId,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        personId: formData.personId ? parseInt(formData.personId) : null,
      };

      if (dialogMode === 'create') {
        await residentEventsApi.create(payload);
        enqueueSnackbar('Event created successfully', { variant: 'success' });
      } else {
        await residentEventsApi.update(editingEvent.id, payload);
        enqueueSnackbar('Event updated successfully', { variant: 'success' });
      }

      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to save event', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    try {
      await residentEventsApi.delete(eventToDelete.id);
      enqueueSnackbar('Event deleted', { variant: 'success' });
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      fetchEvents();
    } catch (err) {
      enqueueSnackbar('Failed to delete event', { variant: 'error' });
    }
  };

  const handleViewSignups = async (event) => {
    setSignupsEvent(event);
    setSignupsDialogOpen(true);
    setSignupsLoading(true);
    try {
      const response = await residentEventsApi.getSignups(event.id);
      setSignups(response.data?.data || []);
    } catch (err) {
      enqueueSnackbar('Failed to load signups', { variant: 'error' });
    } finally {
      setSignupsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getEventTypeConfig = (type) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0];
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Events & Activities
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchEvents}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Event
          </Button>
        </Stack>
      </Box>

      {loading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 3 }} />}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && events.length === 0 && !error && (
        <Alert severity="info">
          No events for this location yet. Create activities, appointments, or reminders for residents.
        </Alert>
      )}

      {events.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Starts</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell align="center">Signups</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => {
                const typeConfig = getEventTypeConfig(event.eventType);
                return (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 4, height: 32, borderRadius: 1,
                          bgcolor: event.color || typeConfig.color
                        }} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {event.title}
                          </Typography>
                          {event.description && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                              {event.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={typeConfig.label}
                        size="small"
                        sx={{ bgcolor: typeConfig.color + '20', color: typeConfig.color, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {event.allDay ? 'All day' : formatDate(event.startsAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {event.locationName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {event.requiresSignup ? (
                        <Chip
                          label={`${event.signupCount || 0}${event.maxParticipants ? `/${event.maxParticipants}` : ''}`}
                          size="small"
                          color={(event.signupCount || 0) > 0 ? 'primary' : 'default'}
                          onClick={() => handleViewSignups(event)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {event.requiresSignup && (
                          <Tooltip title="View signups">
                            <IconButton size="small" onClick={() => handleViewSignups(event)}>
                              <PeopleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit event">
                          <IconButton size="small" onClick={() => handleOpenEdit(event)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete event">
                          <IconButton size="small" color="error" onClick={() => { setEventToDelete(event); setDeleteDialogOpen(true); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Create Event' : 'Edit Event'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              select
              label="Event Type"
              value={formData.eventType}
              onChange={(e) => setFormData(prev => ({ ...prev, eventType: e.target.value }))}
              fullWidth
            >
              {EVENT_TYPES.map((et) => (
                <MenuItem key={et.value} value={et.value}>{et.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Resident (optional, leave empty for location-wide)"
              value={formData.personId}
              onChange={(e) => setFormData(prev => ({ ...prev, personId: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">Location-wide event</MenuItem>
              {persons.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.friendlyName || `Person #${p.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Start Time"
              type="datetime-local"
              value={formData.startsAt}
              onChange={(e) => setFormData(prev => ({ ...prev, startsAt: e.target.value }))}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="datetime-local"
              value={formData.endsAt}
              onChange={(e) => setFormData(prev => ({ ...prev, endsAt: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Venue / Location Name"
              placeholder="e.g. Activity room, Room 204"
              value={formData.locationName}
              onChange={(e) => setFormData(prev => ({ ...prev, locationName: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Max Participants"
                type="number"
                value={formData.maxParticipants}
                onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: e.target.value }))}
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                select
                label="Requires Signup"
                value={formData.requiresSignup ? 'yes' : 'no'}
                onChange={(e) => setFormData(prev => ({ ...prev, requiresSignup: e.target.value === 'yes' }))}
                sx={{ flex: 1 }}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.title || !formData.startsAt}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{eventToDelete?.title}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signups Dialog */}
      <Dialog open={signupsDialogOpen} onClose={() => setSignupsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Signups for {signupsEvent?.title}
          <Typography variant="caption" display="block" color="text.secondary">
            {signupsEvent?.maxParticipants ? `${signups.length}/${signupsEvent.maxParticipants} spots` : `${signups.length} signed up`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {signupsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : signups.length === 0 ? (
            <Alert severity="info">No signups yet.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resident</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Signed Up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {signups.map((signup) => (
                  <TableRow key={signup.id}>
                    <TableCell>{signup.personName || `Person #${signup.personId}`}</TableCell>
                    <TableCell>
                      <Chip
                        label={signup.status}
                        size="small"
                        color={signup.status === 'confirmed' ? 'success' : signup.status === 'waitlisted' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDate(signup.signedUpAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignupsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationEventsTab;
